import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// UI-5 / TD-V2-AUTO-21: listener global pra notification type='celebracao'
// — dispara confete + overlay quando backend cria celebração.
import { CelebrationListener } from '@/components/layout/CelebrationListener';
// R6.5 / FRENTE D: toast persistente quando há nova versão do SW.
import { PwaUpdateToast } from '@/components/PwaUpdateToast';
// FASE 2 / M1 + M2 (auditoria 2026-05-21): providers globais.
import { ConfirmDialog, ToastProvider } from '@/components/ui';
import { AvisoDeCargaGlobal } from '@/components/AvisoDeCargaGlobal';
import { RequireAuth } from '@/components/RequireAuth';
// P2 (AJUSTES-POS-2): gate admin-only pras ferramentas de dev (/dev/ui).
import { RequireAdmin } from '@/components/RequireAdmin';
// B1.5 + B1.6 Portal Cliente — auth context + layout + 4 pages auth + dashboard + chamados.
import { ClienteAuthProvider } from '@/contexts/ClienteAuthContext';
import { ClienteProvider } from '@/contexts/ClienteContext';
import { ClienteLayout } from '@/pages/cliente/ClienteLayout';
import { RequireClienteAuth } from '@/pages/cliente/RequireClienteAuth';
import { LoginPage as ClienteLoginPage } from '@/pages/cliente/LoginPage';
import { AceitarConvitePage as ClienteAceitarConvitePage } from '@/pages/cliente/AceitarConvitePage';
import { RecuperarSenhaPage as ClienteRecuperarSenhaPage } from '@/pages/cliente/RecuperarSenhaPage';
import { RedefinirSenhaPage as ClienteRedefinirSenhaPage } from '@/pages/cliente/RedefinirSenhaPage';
// B1.6: substitui placeholder pelo dashboard real + adiciona pages chamados.
import { DashboardPage as ClienteDashboardPage } from '@/pages/cliente/DashboardPage';
import { AbrirChamadoPage as ClienteAbrirChamadoPage } from '@/pages/cliente/AbrirChamadoPage';
import { ChamadoDetailPage as ClienteChamadoDetailPage } from '@/pages/cliente/ChamadoDetailPage';
import { MeusChamadosPage as ClienteMeusChamadosPage } from '@/pages/cliente/MeusChamadosPage';
import { ChamadosPage as ClienteChamadosPage } from '@/pages/cliente/ChamadosPage';
// B1.7: Gestor-only — orçamentos / contratos / manutenções / financeiro
import { ContratosPage as ClienteContratosPage } from '@/pages/cliente/ContratosPage';
import { ManutencoesPage as ClienteManutencoesPage } from '@/pages/cliente/ManutencoesPage';
import { ProjetosPage as ClienteProjetosPage } from '@/pages/cliente/ProjetosPage';
import { NotificacoesPage as ClienteNotificacoesPage } from '@/pages/cliente/NotificacoesPage';
import { FinanceiroPage as ClienteFinanceiroPage } from '@/pages/cliente/FinanceiroPage';
// B1.8: equipe (Gestor) + perfil (todos)
import { EquipePage as ClienteEquipePage } from '@/pages/cliente/EquipePage';
import { PerfilPage as ClientePerfilPage } from '@/pages/cliente/PerfilPage';
import { LoginPage } from '@/pages/login/LoginPage';
import { HomePage } from '@/pages/home/HomePage';
import { MenuPage } from '@/pages/menu/MenuPage';
import { UiShowcase } from '@/pages/dev/UiShowcase';
import { AdminLayout } from '@/pages/admin/AdminLayout';
// FEAT-V2-037 Fase A: perfis de uso (Cowork 2026-05-25).
import { CatalogsPage } from '@/pages/admin/CatalogsPage';
// CA3: Configurações › Clientes — central de catálogos de contrato + alertas.
// B2 Lote 2 #20: /admin/clientes (V1, R0 legado) redireciona pra clientes-v2
// (canônico). ClientsPage.tsx removido de vez na Rodada C-LIMPEZA (2026-07-10).
// R-IMPORT 2 (2026-05-26): UI cadastro pras 6 entidades novas do R-IMPORT 1.
// Faxina2 / FB1: CatalogsR2Page agora vive embutida em CatalogsPage (área
// "Avançados"). A rota /admin/catalogos-r2 redireciona pra /admin/catalogos.
// Faxina2 / FB2: cadastro das empreiteiras que a IGM contrata.
import { ClientesV2Page } from '@/pages/admin/ClientesV2Page';
// R-IMPORT 2.x (2026-05-26): RBAC granular + detail pages.
import { RequireProfile } from '@/components/RequireProfile';
import { TELA_SLUGS_VISIVEIS } from '@/lib/use-pode-editar';
// B2.1 #7 (2026-05-29): overview das integrações — destino do grupo "Integrações".
// R-IMPORT 3 A1.9 + A1.10 (2026-05-26): admin Omie + admin Produtos.
import { OmieAtivarWritePage } from '@/pages/admin/OmieAtivarWritePage';
// A2.2 Sprint 4 Financeiro (2026-05-26): 9 KPIs MVP.
// B3.3 LAZY: FinanceiroDashboard carrega recharts (BarChart + LineChart + Pie) — pesado pra render inicial.
const FinanceiroDashboardPage = lazy(() =>
  import('@/pages/admin/FinanceiroDashboardPage').then((m) => ({
    default: m.FinanceiroDashboardPage,
  })),
);
// CRM v2 (rodada COMERCIAL-REBUILD): telas do Comercial novo — lazy (dnd-kit
// + gráficos). Rotas em /admin/comercial/* (module mode, padrão Financeiro).
const CrmLeadsPage = lazy(() => import('@/pages/comercial/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const CrmFunilPage = lazy(() => import('@/pages/comercial/FunilPage').then((m) => ({ default: m.FunilPage })));
const CrmIndicadoresPage = lazy(() =>
  import('@/pages/comercial/IndicadoresPage').then((m) => ({ default: m.IndicadoresPage })),
);
const CrmMetasPage = lazy(() => import('@/pages/comercial/MetasPage').then((m) => ({ default: m.MetasPage })));
const CrmExclusoesPage = lazy(() => import('@/pages/admin/comercial/ExclusoesPage'));
const ConfigCrmPage = lazy(() => import('@/pages/comercial/ConfigCrmPage').then((m) => ({ default: m.ConfigCrmPage })));
// Bloco D (V2-HORARIO-FUNCIONAMENTO): config global de expediente + feriados.
const HorarioFuncionamentoPage = lazy(() =>
  import('@/pages/admin/HorarioFuncionamentoPage').then((m) => ({ default: m.HorarioFuncionamentoPage })),
);
// Orçamento v2 (O1): submenu Orçamentos — lista por proposta (casa nova).
const CrmOrcamentosListaPage = lazy(() =>
  import('@/pages/comercial/OrcamentosListaPage').then((m) => ({ default: m.OrcamentosListaPage })),
);
// Rodada 3: simulador de cenários (inconformidades virou aba do dashboard — pós-R3 L2).
const FinanceiroSimuladorPage = lazy(() =>
  import('@/pages/admin/financeiro/FinanceiroSimuladorPage').then((m) => ({
    default: m.FinanceiroSimuladorPage,
  })),
);
// Pós-R3 L1: configurações do Financeiro em página própria (Contas + Metas).
// A3.2 Visual Builder Nivel 3 (2026-05-26): home modular drag-drop.
// B3.3 LAZY: VisualBuilder carrega react-grid-layout (~80KB) — lazy reduz bundle inicial.
const VisualBuilderPage = lazy(() =>
  import('@/pages/admin/dashboard/VisualBuilderPage').then((m) => ({
    default: m.VisualBuilderPage,
  })),
);
// A4 R-PROJETOS (2026-05-27): UI Projetos — lista + wizard + (detail + tipos próximas sub-fases).
import { ProjetosListPage } from '@/pages/admin/projetos/ProjetosListPage';
// P1 (Projetos 2.0 Parte 2): quadro de pendentes (gated por PROJETOS_V2_ENABLED).
// P7 (Projetos 2.0 Parte 2): catálogo de "Situação do material" (logística, gated).
// B3.3 LAZY: Detail de 5 abas + recharts → lazy.
// Parte 3 / F1: NovoProjetoWizard (/admin/projetos/novo) aposentado — projeto
// nasce da venda em Ganho. Arquivo removido na Rodada C-LIMPEZA (2026-07-10).
const ProjetoDetailPage = lazy(() =>
  import('@/pages/admin/projetos/ProjetoDetailPage').then((m) => ({
    default: m.ProjetoDetailPage,
  })),
);
import { ProjetoTiposPage } from '@/pages/admin/projetos/ProjetoTiposPage';
import type { ProfileSlug } from '@/lib/admin-types';
// R-IMPORT 2.x detail pages (5 entidades com tabs):
// B3.3 LAZY: 5 detail pages × ~10 KB cada = ~50 KB tirados do bundle inicial.
const ClientDetailPage = lazy(() =>
  import('@/pages/admin/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })),
);
import { AdminConfigPage } from '@/pages/admin/AdminConfigPage';
import { ConfiguracoesPage } from '@/pages/admin/configuracoes/ConfiguracoesPage';
import { SecaoIndice } from '@/pages/admin/configuracoes/SecaoIndice';
import { ItemPainel } from '@/pages/admin/configuracoes/ItemPainel';
import { REDIRECTS_CONFIG } from '@/pages/admin/configuracoes/redirects';
import { WorkflowsPage } from '@/pages/admin/WorkflowsPage';
import { TipoAtividadeAutomacoesPage } from '@/pages/admin/TipoAtividadeAutomacoesPage';
import { QuestionariosPage } from '@/pages/admin/QuestionariosPage';
// FEAT-V2-019 UI: painel de gestão de feature flags em runtime.
// FEAT-V2-030: visão global de automações across workflows.
// FEAT-V2-031: catálogo de tags admin-managed.
import { TecnicoPage } from '@/pages/tecnico/TecnicoPage';
import { FormCaptacaoPage } from '@/pages/publico/FormCaptacaoPage';
import { OsPublicaPage } from '@/pages/publico/OsPublicaPage';
// TESTES DISC e QA: o teste pelo link individual (sem login).
import { TestePublicoPage } from '@/pages/publico/TestePublicoPage';
import { FeedbackPublicoPage } from '@/pages/publico/FeedbackPublicoPage';
import { NotificationsPage } from '@/pages/perfil/NotificationsPage';
import { PerfilPage } from '@/pages/perfil/PerfilPage';
import { MaisPage } from '@/pages/mais/MaisPage';
// B-FINAL B3.1.0 — destrava frontend Portal Ajuda (backend A2.4 já em prod).
// Comunicados: tela de uso ocasional, fora da rota crítica.
const ComunicadosPage = lazy(() => import('@/pages/ComunicadosPage'));
// RH-MENU (03/10/2026): o RH — atrás da flag MENU_RH_ENABLED (desligada).
const RhListaPage = lazy(() => import('@/pages/rh/RhListaPage'));
const RhNovoPage = lazy(() => import('@/pages/rh/RhNovoPage'));
const RhFichaPage = lazy(() => import('@/pages/rh/RhFichaPage'));
const PainelFeedbackPage = lazy(() => import('@/pages/rh/PainelFeedbackPage'));
const ComunicadoPage = lazy(() => import('@/pages/ComunicadoPage'));
// Manutencao > Indicadores: a visao de gestao, fora das telas de fila.
const IndicadoresPage = lazy(() => import('@/pages/manutencao/IndicadoresPage'));
// OS DO SISTEMA (fase 3, 25/10/2026): Agenda, Chamados de OS, Relatórios de OS e o relatório público do cliente.
const OsAgendaPage = lazy(() => import('@/pages/os-sistema/AgendaPage'));
const PlanejarPreventivasPage = lazy(() => import('@/pages/os-sistema/PlanejarPreventivasPage'));
const OsChamadosPage = lazy(() => import('@/pages/os-sistema/ChamadosDeOsPage'));
const OsRelatoriosPage = lazy(() => import('@/pages/os-sistema/RelatoriosDeOsPage'));
const OsRelatorioPublicoPage = lazy(() => import('@/pages/os-sistema/RelatorioPublicoPage'));
// ORÇAMENTO DA MANUTENÇÃO (mig 782): a página que o botão do e-mail abre (sem login, por token).
const AprovacaoDoOrcamentoPage = lazy(() => import('@/pages/publico/AprovacaoDoOrcamentoPage'));
const PesquisaPublicaPage = lazy(() => import('@/pages/publico/PesquisaPublicaPage'));
// OS DO SISTEMA (fase 4): o aplicativo do técnico no celular. Tela cheia, sem o menu; o servidor confere o piloto.
const CampoPage = lazy(() => import('@/pages/os-campo/CampoPage'));
const EntrarCampoPage = lazy(() => import('@/pages/os-campo/EntrarCampoPage'));
const DefinirSenhaCampoPage = lazy(() => import('@/pages/os-campo/DefinirSenhaCampoPage'));
// B3.3 LAZY: AjudaPage rendererings markdown — fora da rota crítica.
const AjudaPage = lazy(() =>
  import('@/pages/ajuda/AjudaPage').then((m) => ({ default: m.AjudaPage })),
);
// A6.4 LAZY: builder de campos personalizados (sistema EAV) — admin-only.
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Fallback de loading exibido durante carregamento de chunks lazy (B3.3).
 * Minimal pra evitar layout shift — chunks dessas pages são tipicamente
 * 10-80 KB compressed, carregam em <500ms em 3G.
 */
function LazyFallback() {
  return (
    <div className="flex items-center justify-center p-6 md:p-10 min-h-[200px]">
      <div className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</div>
    </div>
  );
}

/**
 * Root component — define o roteamento por URL.
 *
 * Estrutura de rotas:
 * - /login                       → tela de login (única rota pública)
 * - /                            → Home (protegida)
 * - /menu/:departamento          → tela operacional (protegida)
 * - /portal/*                    → portal do cliente (R5.5, ainda placeholder)
 * - *                            → 404
 *
 * Todas as rotas (exceto /login) exigem usuário autenticado via <RequireAuth>.
 * Se não estiver logado, redireciona pra /login preservando a URL de origem.
 */
export default function App() {
  return (
    <ToastProvider>
      {/* D-3: carga que falha AVISA (toast global dedupado — aviso-carga.ts). */}
      <AvisoDeCargaGlobal />
      <ConfirmDialog>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* Públicas (sem auth) */}
        <Route path="/login" element={<LoginPage />} />
        {/* FE2: formulário público de captação — geral (/f/contato) e pessoal (/f/:slug) */}
        <Route path="/f/contato" element={<FormCaptacaoPage />} />
        <Route path="/f/:slug" element={<FormCaptacaoPage />} />
        <Route path="/os/:token" element={<OsPublicaPage />} />
        {/* O relatório da OS do sistema pelo link (sem login, por token). */}
        <Route path="/os-relatorio/:token" element={<OsRelatorioPublicoPage />} />
        {/* A aprovação do orçamento da Manutenção pelo link do e-mail (sem login, por token; abrir não aprova). */}
        <Route path="/orcamento/:token" element={<AprovacaoDoOrcamentoPage />} />
        <Route path="/pesquisa/:token" element={<PesquisaPublicaPage />} />
        {/* O técnico sem e-mail da empresa: entrar com e-mail e senha, e criar a senha pelo link do convite (25/10/2026). */}
        <Route path="/campo/entrar" element={<EntrarCampoPage />} />
        <Route path="/campo/definir-senha/:token" element={<DefinirSenhaCampoPage />} />
        {/* O aplicativo do técnico (login exigido; o servidor confere se a pessoa está no piloto). */}
        <Route
          path="/campo"
          element={
            <RequireAuth>
              <CampoPage />
            </RequireAuth>
          }
        />
        <Route path="/teste/:token" element={<TestePublicoPage />} />
        {/* FEEDBACK (C1): a autoavaliação e o plano pelo link individual (sem login), mesmo padrão do teste. */}
        <Route path="/feedback/:token" element={<FeedbackPublicoPage />} />

        {/* B1.5 Portal Cliente — todas as rotas /cliente/* dentro do
            ClienteAuthProvider que hidrata sessão via cookie httpOnly.
            Rotas internas (login/aceitar/recuperar/redefinir) são públicas;
            /dashboard exige auth via RequireClienteAuth. */}
        <Route
          path="/cliente"
          element={
            <ClienteAuthProvider>
              <ClienteProvider>
                <ClienteLayout />
              </ClienteProvider>
            </ClienteAuthProvider>
          }
        >
          <Route index element={<Navigate to="/cliente/login" replace />} />
          <Route path="login" element={<ClienteLoginPage />} />
          {/* (terceiro routes ficam fora deste bloco — ver abaixo) */}
          <Route path="aceitar-convite/:token" element={<ClienteAceitarConvitePage />} />
          <Route path="recuperar-senha" element={<ClienteRecuperarSenhaPage />} />
          <Route path="redefinir-senha/:token" element={<ClienteRedefinirSenhaPage />} />
          <Route
            path="dashboard"
            element={
              <RequireClienteAuth>
                <ClienteDashboardPage />
              </RequireClienteAuth>
            }
          />
          {/* B1.6: chamados (abrir + meus + todos + detail) */}
          <Route
            path="chamado/novo"
            element={
              <RequireClienteAuth>
                <ClienteAbrirChamadoPage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="chamado/:id"
            element={
              <RequireClienteAuth>
                <ClienteChamadoDetailPage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="meus-chamados"
            element={
              <RequireClienteAuth>
                <ClienteMeusChamadosPage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="chamados"
            element={
              <RequireClienteAuth>
                <ClienteChamadosPage />
              </RequireClienteAuth>
            }
          />
          {/* ⛔ D76 (27/10/2026): os orçamentos saíram do Portal. Os endereços antigos (link em e-mail, favorito) levam ao
              início do Portal em vez de dar página não encontrada. */}
          <Route path="orcamentos" element={<Navigate to="/cliente/dashboard" replace />} />
          <Route path="orcamento/:id" element={<Navigate to="/cliente/dashboard" replace />} />
          <Route
            path="contratos"
            element={
              <RequireClienteAuth>
                <ClienteContratosPage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="manutencoes"
            element={
              <RequireClienteAuth>
                <ClienteManutencoesPage />
              </RequireClienteAuth>
            }
          />
          {/* F3.1 — Gestor acompanha projetos (etapas/datas/%, sem valores) */}
          <Route
            path="projetos"
            element={
              <RequireClienteAuth>
                <ClienteProjetosPage />
              </RequireClienteAuth>
            }
          />
          {/* F3.2 — histórico de notificações (sino no header) */}
          <Route
            path="notificacoes"
            element={
              <RequireClienteAuth>
                <ClienteNotificacoesPage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="financeiro"
            element={
              <RequireClienteAuth>
                <ClienteFinanceiroPage />
              </RequireClienteAuth>
            }
          />
          {/* B1.8 — Gestor only (equipe) + todos (perfil) */}
          <Route
            path="equipe"
            element={
              <RequireClienteAuth>
                <ClienteEquipePage />
              </RequireClienteAuth>
            }
          />
          <Route
            path="perfil"
            element={
              <RequireClienteAuth>
                <ClientePerfilPage />
              </RequireClienteAuth>
            }
          />
        </Route>

        {/* Protegidas */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <HomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/menu/:departamento/:workflow?"
          element={
            <RequireAuth>
              <MenuPage />
            </RequireAuth>
          }
        />

        {/* Redirects */}
        <Route path="/menu" element={<Navigate to="/menu/comercial" replace />} />
        {/* FASE-C: /dashboard/:submenu era 8 cards "Indicador N · aguarda
            integração" + "URL atual:" — tela 100% placeholder. Removida junto
            com a entrada da Sidebar e do /mais. Os indicadores que existem de
            verdade moram em /admin/comercial/indicadores e
            /admin/financeiro/dashboard. */}

        {/* Admin (protegidas — só admins) */}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/admin/usuarios" replace />} />
          {/* FEAT-V2-037 Fase A: perfis de uso (Cowork 2026-05-25). */}
          <Route path="catalogos" element={<CatalogsPage />} />
          {/* CA3: Configurações › Clientes — central de catálogos de contrato + alertas. */}
          {/* B2.1 #7: overview das integrações (destino do grupo "Integrações"). */}
          {/* R-IMPORT 3 A1.9 / A1.10: admin Omie + admin Produtos universal/conciliar. */}
          <Route path="omie/ativar-write" element={<OmieAtivarWritePage />} />
          {/* CRM v2 (COMERCIAL-REBUILD): Comercial novo — Leads/Funil/Indicadores/Metas + config. */}
          <Route path="comercial" element={<Navigate to="/admin/comercial/funil" replace />} />
          <Route path="comercial/leads" element={<CrmLeadsPage />} />
          <Route path="comercial/funil" element={<CrmFunilPage />} />
          <Route path="comercial/indicadores" element={<CrmIndicadoresPage />} />
          <Route path="comercial/metas" element={<CrmMetasPage />} />
          {/* BLOCO L (26/09/2026): a prestação de contas de quem exclui. A própria
              rota da API confere a permissão — esconder o item do menu é
              conveniência, não segurança. */}
          <Route path="comercial/exclusoes" element={<CrmExclusoesPage />} />
          <Route path="comercial/orcamentos" element={<CrmOrcamentosListaPage />} />
          <Route path="comercial/crm" element={<ConfigCrmPage />} />
          {/* A2.2 Sprint 4 Financeiro: dashboard 9 KPIs. */}
          <Route path="financeiro/dashboard" element={<FinanceiroDashboardPage />} />
          <Route path="financeiro/simulador" element={<FinanceiroSimuladorPage />} />
          {/* A3.2 Visual Builder Nivel 3: home modular drag-drop. */}
          <Route path="dashboard/builder" element={<VisualBuilderPage />} />
          {/* A4 R-PROJETOS: lista de projetos + wizard novo (detail/admin tipos próximas sub-fases). */}
          <Route path="projetos" element={<ProjetosListPage />} />
          {/* PROJETOS-LIMPO 2 (14/09/2026): a página de pendentes virou a seção "Esperando
              começar" no topo da lista — uma porta só. Link antigo cai na lista. */}
          <Route path="projetos/pendentes" element={<Navigate to="/admin/projetos" replace />} />
          {/* FAXINA 2026-07-31 (D-11): os catálogos de atividades (P3) e de tipos de
              documento (P4) de Projetos viviam em DOIS endereços — aqui e embutidos em
              Catálogos › Projetos, que é o único com entrada de menu. Mesmo componente,
              dois caminhos. As rotas diretas agora redirecionam, igual `catalogos-r2`. */}
          <Route path="projeto-atividades" element={<Navigate to="/admin/catalogos" replace />} />
          <Route path="projeto-documento-tipos" element={<Navigate to="/admin/catalogos" replace />} />
          {/* SU-3: rota material-situacoes removida (catálogo P7 aposentado). */}
          <Route path="projetos/:id" element={<ProjetoDetailPage />} />
          <Route path="projeto-tipos" element={<ProjetoTiposPage />} />
          {/* Faxina2 / FB1: "Catálogos R2" foi unificado em Catálogos (área
              "Avançados", RBAC administrativo preservado lá dentro). A rota
              antiga redireciona — links/bookmarks externos não quebram. */}
          <Route path="catalogos-r2" element={<Navigate to="/admin/catalogos" replace />} />
          {/* B2 Lote 2 #20: legado V1 redireciona pro canônico (sem órfã URL-only). */}
          <Route path="clientes" element={<Navigate to="/admin/clientes-v2" replace />} />
          {/* Faxina2 / FB2: cadastro das empreiteiras que a IGM contrata. */}
          {/* RESPONSAVEIS-NA-FICHA (07/08) — decisão do Matheus: as telas de
              Responsáveis saíram. O cadastro NÃO foi apagado: criar, vincular,
              EDITAR e REMOVER a pessoa vivem agora dentro da ficha do cliente
              (aba Contatos), que é por onde o time já usava. Endpoint,
              repositório e tabela continuam de pé — a ficha depende deles. */}
          <Route
            path="clientes-v2"
            element={
              <RequireProfile slugs={TELA_SLUGS_VISIVEIS.clientes as ProfileSlug[]}>
                <ClientesV2Page />
              </RequireProfile>
            }
          />
          {/* ROTAS-ORFAS (06/08): /admin/contratos (LEGADO) removida — 0
              contratos na tabela antiga; a versão viva é `contratos_v2`, com
              tela própria. A rota do PORTAL DO CLIENTE (mesmo path, outro
              bloco) e a tabela continuam: o portal é operacional. */}
          {/* Contratos Master, Empresas Contratantes e os redirects de
              `contratos-master` saíram em 2026-08-01, com o conceito de cliente
              terceirizado. Não ganharam redirect novo: não há para onde mandar,
              e redirect que cai em lugar nenhum é pior que o 404 honesto. Rota
              sem chave mapeada é admin-only por padrão — nada abre por acidente. */}
          {/* R-IMPORT 2.x: detail pages (5 entidades com tabs). */}
          <Route
            path="clientes-v2/:id"
            element={
              <RequireProfile slugs={TELA_SLUGS_VISIVEIS.clientes as ProfileSlug[]}>
                <ClientDetailPage />
              </RequireProfile>
            }
          />

          {/* A Configurações nova: menu lateral de seções, uma URL por seção.
              `/admin/configuracoes` abre a primeira seção que a pessoa pode ver.
              A `AdminConfigPage` (regras gerais, Financeiro, Segurança) continua
              viva em `/admin/configuracoes/geral` — vários itens do catálogo
              apontam pras âncoras dela. */}
          {/* K-1 (CASCA): a `ConfiguracoesPage` virou LAYOUT. Ela renderiza a
              moldura — cabeçalho, busca e menu de seções — e o painel direito é
              `<Outlet/>`. As filhas abaixo decidem o que aparece ali.

              O ponto de tudo isto: a moldura NÃO remonta ao trocar de painel.
              Enquanto era uma rota só, cada clique repintava o menu inteiro. */}
          <Route path="configuracoes" element={<ConfiguracoesPage />}>
            <Route index element={<SecaoIndice />} />
            {/* Estática antes da dinâmica: `geral` e `horario-funcionamento`
                são itens de verdade e não podem ser engolidos por `:secao`. */}
            <Route path=":secao" element={<SecaoIndice />} />
            <Route path=":secao/:item" element={<ItemPainel />} />
          </Route>
          {/* K-2: as rotas ANTIGAS redirecionam pras novas — não somem.
              Há favorito, link em artigo de ajuda e link em notificação
              apontando pra elas, e já tivemos link morto na sidebar por
              remover cedo demais.

              A lista é GERADA do catálogo (`REDIRECTS_CONFIG`), não escrita à
              mão: uma segunda lista de rotas divergiria da primeira no dia em
              que alguém mexesse só numa. */}
          {REDIRECTS_CONFIG.map(({ de, para }) => (
            <Route key={de} path={de} element={<Navigate to={para} replace />} />
          ))}
          {/* Estas DUAS continuam fora da casca por enquanto — são as telas do
              caso 2 do K-3 (as âncoras da `AdminConfigPage` e o horário), e
              migram com a seção delas. Ficam declaradas DEPOIS do bloco acima
              mas o React Router ranqueia estática acima de dinâmica, então
              `/configuracoes/geral` não é engolida por `:secao`. */}
          <Route path="configuracoes/geral" element={<AdminConfigPage />} />
          {/* Bloco D: horário de funcionamento (config global — expediente + feriados). */}
          <Route
            path="configuracoes/horario-funcionamento"
            element={<HorarioFuncionamentoPage />}
          />
          {/* ROTAS-ORFAS (06/08) — decisão 3 do Matheus: a tela mais sensível
              das sete (8 sequências; o Comercial roda em cima de uma delas).
              VISÍVEL E EDITÁVEL SÓ POR ADMIN.

              No SERVIDOR, criar/editar/apagar já é `requireAdmin`
              (`server/routes/workflows.ts`) — e é lá que a restrição de fato
              vale. A LEITURA da lista continua `requireAuth` DE PROPÓSITO: a
              Sidebar de todo mundo chama `useWorkflows` para montar os
              subworkflows, e fechá-la derrubaria a navegação de quem não é
              admin — o erro do Compras ao contrário. */}
          <Route
            path="workflows"
            element={
              <RequireAdmin>
                <WorkflowsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="tipos-atividade/:id/automacoes"
            element={<TipoAtividadeAutomacoesPage />}
          />
          <Route path="questionarios" element={<QuestionariosPage />} />
          {/* FEAT-V2-019 UI: painel de feature flags. */}
          {/* AUTOMACOES-SAI (07/08/2026): a tela de administração das
              automações saiu — `automations` tem 0 regras, então não havia o
              que administrar. Volta quando a Manutenção subir.

              ⚠️ NÃO confundir com `automacoes` (CRM), homônimo que FICA: é
              outra tabela, outra tela. Foi esse par de nomes que fez a contagem
              sair errada no levantamento (BUGS #83).

              O motor segue de pé: `AutomationsPanel` é usado por Compras e
              Manutenção, e o endpoint continua servindo. */}
          {/* FEAT-V2-031: catálogo de tags. */}
          {/* A6.4 — Builder de campos personalizados (sistema EAV). */}
        </Route>

        {/* Modo Técnico (R5) — UI minimalista mobile-first separada do AppShell */}
        <Route
          path="/tecnico"
          element={
            <RequireAuth>
              <TecnicoPage />
            </RequireAuth>
          }
        />


        {/* Mais — gaveta de navegação secundária (mobile-first, BUG-V2-002) */}
        <Route
          path="/mais"
          element={
            <RequireAuth>
              <MaisPage />
            </RequireAuth>
          }
        />

        {/* Portal de Ajuda — A2.4 backend + B-FINAL B3.1.0 frontend. Gating
            por perfil (Regra #14) acontece server-side em
            lib/portal-ajuda/buscar.ts. /cliente/ajuda pendente B1. */}
        <Route
          path="/ajuda"
          element={
            <RequireAuth>
              <AjudaPage />
            </RequireAuth>
          }
        />

        {/* Comunicados (mig 542) — item próprio, não de departamento.
            ⚠️ Quem barra de verdade é `requireTela('comunicados.ver')` na API;
            o `acesso-rotas.ts` faz o front respeitar a tela de Permissões. */}
        <Route
          path="/comunicados"
          element={
            <RequireAuth>
              <ComunicadosPage />
            </RequireAuth>
          }
        />
        {/* 16/09: a página do comunicado (escrever → para quem → quando → conferir) — no lugar dos cinco modais. */}
        <Route
          path="/comunicados/:id"
          element={
            <RequireAuth>
              <ComunicadoPage />
            </RequireAuth>
          }
        />

        {/* RH (RH-MENU, 03/10/2026). Item próprio da seção Apoio, não de
            departamento. ⚠️ Quem barra é o servidor (`/api/v1/rh`, flag +
            `podeAbrirRh` + quem vê qual ficha); a tela só mostra a resposta. */}
        <Route
          path="/rh"
          element={
            <RequireAuth>
              <RhListaPage />
            </RequireAuth>
          }
        />
        {/* FEEDBACK (C1): o Painel de feedback mora dentro do RH (decisão de 05/10). Quem barra é o servidor. */}
        <Route
          path="/rh/feedback"
          element={
            <RequireAuth>
              <PainelFeedbackPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rh/novo"
          element={
            <RequireAuth>
              <RhNovoPage />
            </RequireAuth>
          }
        />
        <Route
          path="/rh/:id"
          element={
            <RequireAuth>
              <RhFichaPage />
            </RequireAuth>
          }
        />

        {/* Manutenção › Indicadores (mig 547). ⚠️ A rota está em
            `acesso-rotas.ts` — sem aquela linha, abriria só para admin. */}
        <Route
          path="/menu/manutencao/indicadores"
          element={
            <RequireAuth>
              <IndicadoresPage />
            </RequireAuth>
          }
        />
        {/* OS do sistema (mig 772). ⚠️ As rotas estão em `acesso-rotas.ts` (chave `manutencao.os.ver`). */}
        <Route
          path="/menu/manutencao/agenda"
          element={
            <RequireAuth>
              <OsAgendaPage />
            </RequireAuth>
          }
        />
        {/* D72 (mig 789): o planejamento das preventivas. Achado 12 (28/10): virou o submenu "Planejador"; o endereço
            antigo leva a ele (link salvo não quebra). Chave `manutencao.os.gerir`. */}
        <Route
          path="/menu/manutencao/planejador"
          element={
            <RequireAuth>
              <PlanejarPreventivasPage />
            </RequireAuth>
          }
        />
        <Route path="/menu/manutencao/planejar-preventivas" element={<Navigate to="/menu/manutencao/planejador" replace />} />
        <Route
          path="/menu/manutencao/chamados-os"
          element={
            <RequireAuth>
              <OsChamadosPage />
            </RequireAuth>
          }
        />
        <Route
          path="/menu/manutencao/relatorios-os"
          element={
            <RequireAuth>
              <OsRelatoriosPage />
            </RequireAuth>
          }
        />

        {/* Perfil pessoal (protegido) */}
        <Route
          path="/perfil"
          element={
            <RequireAuth>
              <PerfilPage />
            </RequireAuth>
          }
        />
        <Route
          path="/perfil/notificacoes"
          element={
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          }
        />

        {/* Dev tools — só admins (P2: gate admin aplicado; antes qualquer logado entrava). */}
        <Route
          path="/dev/ui"
          element={
            <RequireAdmin>
              <UiShowcase />
            </RequireAdmin>
          }
        />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>

      {/* Versão sempre visível no rodapé */}

      {/* UI-5: listener global de celebrações — fora do Routes pra
          permanecer ativo em qualquer rota autenticada. Como depende de
          useAuth, em rotas públicas (anonymous user) é no-op. */}
      <CelebrationListener />

      {/* R6.5 / FRENTE D: toast "Nova versão disponível" — útil em
          qualquer rota (logada ou pública), por isso fora de RequireAuth. */}
      <PwaUpdateToast />
      </ConfirmDialog>
    </ToastProvider>
  );
}
